import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Send, CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { PUBLIC_API } from "../lib/api";
import TeroumaLogo from "../components/ui/TeroumaLogo";

export default function OrganizationContactPage() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [loadingOrg, setLoadingOrg] = useState(true);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${PUBLIC_API}/organizations/public/${orgId}/profile`)
      .then(r => setOrg(r.data))
      .catch(() => {})
      .finally(() => setLoadingOrg(false));
  }, [orgId]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.message) {
      toast.error("Remplissez tous les champs obligatoires.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${PUBLIC_API}/organizations/public/${orgId}/contact`, form);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Une erreur s'est produite. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#F4F3EF" }}>
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-stone-200">
        <div className="max-w-xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/"><TeroumaLogo textColor="#0D1B33" /></Link>
          <button onClick={() => navigate(`/organisations/${orgId}`)} className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-900">
            <ArrowLeft className="h-4 w-4" /> Retour à la fiche
          </button>
        </div>
      </nav>

      <div className="max-w-xl mx-auto px-4 py-12">
        {done ? (
          <div className="bg-white rounded-3xl p-10 text-center shadow-sm border border-stone-100">
            <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="h-8 w-8 text-emerald-500" />
            </div>
            <h2 className="text-2xl font-semibold text-stone-900 mb-2">Message envoyé !</h2>
            <p className="text-stone-500 text-sm mb-6">
              Votre message a bien été transmis à {org?.name || "l'organisation"}.
              Vous recevrez une confirmation par email.
            </p>
            <Link to={`/organisations/${orgId}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl text-white text-sm font-medium"
              style={{ background: "#0D1B33" }}>
              <ArrowLeft className="h-4 w-4" /> Retour à la fiche
            </Link>
          </div>
        ) : (
          <div className="bg-white rounded-3xl shadow-sm border border-stone-100 overflow-hidden">
            <div className="h-2" style={{ background: "linear-gradient(90deg, #0D1B33, #B8962E)" }} />
            <div className="p-8">
              <div className="mb-6">
                <h1 className="text-2xl font-semibold text-stone-900 mb-1">Contacter l'organisation</h1>
                {!loadingOrg && org && (
                  <p className="text-stone-500 text-sm">{org.name} · {org.city}</p>
                )}
              </div>

              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-1.5">Prénom *</label>
                    <input value={form.first_name} onChange={e => set("first_name", e.target.value)}
                      className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10" />
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-1.5">Nom *</label>
                    <input value={form.last_name} onChange={e => set("last_name", e.target.value)}
                      className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10" />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-1.5">Email *</label>
                  <input type="email" value={form.email} onChange={e => set("email", e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10" />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-1.5">Téléphone <span className="normal-case tracking-normal text-stone-400">(optionnel)</span></label>
                  <input type="tel" value={form.phone} onChange={e => set("phone", e.target.value)}
                    placeholder="+33 6 12 34 56 78"
                    className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10" />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-1.5">Message *</label>
                  <textarea rows={5} value={form.message} onChange={e => set("message", e.target.value)}
                    placeholder="Votre message…"
                    className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10 resize-none" />
                </div>

                <button type="submit" disabled={submitting}
                  className="w-full py-3.5 rounded-2xl text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
                  style={{ background: "#0D1B33" }}
                  onMouseEnter={e => !submitting && (e.currentTarget.style.background = "#1B2A4A")}
                  onMouseLeave={e => (e.currentTarget.style.background = "#0D1B33")}>
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Envoyer le message
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
